import { notificationRequest } from './api';

const ORIGIN = 'https://wiw.today';
const parse = value => { try { return typeof value === 'string' ? JSON.parse(value) : value; } catch { return null; } };

// Installed at module load, before TWA's NAVIGATION_FINISHED/first transferred port.
// Ordinary web pages have no port and never invoke browser Notification/PushManager.
export function createAndroidBridge(target = window) {
  let port = null, sequence = 0;
  const listeners = new Set(), pending = new Map();
  const emit = value => listeners.forEach(listener => listener(value));
  const disconnect = () => {
    port?.close(); port = null;
    pending.forEach(({ reject, timer }) => { clearTimeout(timer); reject(new Error('PUSH_SESSION_CHANGED')); });
    pending.clear();
  };
  const receive = event => {
    const value = parse(event.data);
    if (!value || typeof value !== 'object') return;
    if (value.type === 'wiw:push:reply') {
      const task = pending.get(value.id);
      if (!task) return;
      pending.delete(value.id); clearTimeout(task.timer);
      if (value.error) task.reject(new Error(/^PUSH_[A-Z_]{1,60}$/.test(value.error) ? value.error : 'PUSH_REGISTRATION_FAILED'));
      else task.resolve(value.result || {});
    } else if (['wiw:push:received', 'wiw:push:token-changed', 'wiw:push:state-changed'].includes(value.type)) emit(value.type);
  };
  const connect = event => {
    const hello = parse(event.data);
    if (target.location.origin !== ORIGIN || event.origin !== ORIGIN || !event.ports?.[0]
      || hello?.type !== 'wiw:push:hello' || hello.version !== 1) return;
    disconnect(); port = event.ports[0]; port.onmessage = receive; port.start(); emit('ready');
  };
  target.addEventListener('message', connect);
  return {
    available: () => !!port,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    call(method, data = {}) {
      if (!port) return Promise.reject(new Error('PUSH_REGISTRATION_UNAVAILABLE'));
      return new Promise((resolve, reject) => {
        const id = String(++sequence);
        const timer = setTimeout(() => { pending.delete(id); reject(new Error('PUSH_REGISTRATION_FAILED')); },
          method === 'permission' ? 60000 : 15000);
        pending.set(id, { resolve, reject, timer });
        try { port.postMessage(JSON.stringify({ ...data, type: 'wiw:push:request', id, method })); }
        catch { pending.delete(id); clearTimeout(timer); reject(new Error('PUSH_REGISTRATION_FAILED')); }
      });
    },
    dispose() { target.removeEventListener('message', connect); disconnect(); listeners.clear(); },
  };
}
const bridge = typeof window !== 'undefined' ? createAndroidBridge() : null;
export const isNativeAndroid = () => !!bridge?.available();
// A display hint only. It never grants access to native APIs: those still require the verified port.
export function androidContainerHint(target = window) {
  try {
    const address = new URL(target.location.href);
    if (address.searchParams.get('wiw_native') === 'android' || target.document.referrer.startsWith('android-app://today.wiw.twa')) {
      target.sessionStorage.setItem('wiw-native-container', 'android');
      address.searchParams.delete('wiw_native'); target.history.replaceState(target.history.state, '', address);
    }
    return target.sessionStorage.getItem('wiw-native-container') === 'android';
  } catch { return false; }
}
const expectedAndroid = typeof window !== 'undefined' && androidContainerHint();
export const isAndroidApp = () => expectedAndroid || isNativeAndroid();

export function createAndroidPush({ transport = bridge, request = notificationRequest,
  onReceived = () => {}, onAction = () => {}, onState = () => {}, expected = isAndroidApp } = {}) {
  let session = null, generation = 0, disposed = false, pending = null;
  let state = { platform: expected() || transport.available() ? 'android' : 'web', permission: 'unknown', registration: 'unavailable' };
  const publish = patch => { if (!disposed) { state = { ...state, ...patch }; onState(state); } };
  const current = version => !disposed && version === generation;
  const check = version => { if (!current(version)) throw new Error('PUSH_SESSION_CHANGED'); };
  const visible = () => {
    if (transport.available()) void transport.call('visible', { visible: document.visibilityState !== 'hidden' }).catch(() => {});
  };
  const refreshStatus = async () => {
    if (!transport.available()) { publish({ platform: expected() ? 'android' : 'web', permission: 'unknown', registration: 'unavailable' }); return; }
    const version = generation;
    try {
      const result = await transport.call('status'); check(version);
      publish({ platform: 'android', permission: result.permission || (result.granted ? 'granted' : 'denied'),
        registration: pending ? 'registering' : result.registered && result.user_id === session?.userId
          ? result.pending ? 'registering' : 'registered' : 'unregistered' });
    } catch { if (current(version)) publish({ registration: 'error' }); }
  };
  const unregister = async (previous, version = generation) => {
    if (!transport.available()) return;
    const state = await transport.call('status');
    check(version);
    await transport.call('clear');
    check(version);
    if (previous && state.installation_id) await request('/devices', { method: 'DELETE', token: previous.token,
      body: { installation_id: state.installation_id } }).catch(() => {});
  };
  async function perform(prompt, version) {
    const owner = session;
    let state = await transport.call('status'); check(version);
    if (prompt && !state.granted && state.permission === 'denied') {
      publish({ platform: 'android', permission: 'denied', registration: 'unregistered' });
      throw new Error('PUSH_SETTINGS_REQUIRED');
    }
    if (prompt && !state.granted) { state = await transport.call('permission'); check(version); }
    publish({ platform: 'android', permission: state.permission || (state.granted ? 'granted' : 'denied'), registration: state.granted ? 'registering' : 'unregistered' });
    if (!prompt && state.granted && state.registered && state.user_id === owner.userId && !state.pending && !state.renew_required) {
      publish({ registration: 'registered' }); return;
    }
    if (!state.granted) {
      await unregister(owner, version); check(version);
      if (prompt) throw new Error(state.permission === 'prompt' ? 'PUSH_PERMISSION_CANCELLED' : 'PUSH_PERMISSION_DENIED');
      return;
    }
    const device = await transport.call('register'); check(version);
    if (device.platform !== 'android' || device.environment !== 'fcm'
      || typeof device.token !== 'string' || !Number.isSafeInteger(device.binding_version)
      || !Number.isSafeInteger(device.token_version)
      || typeof device.installation_id !== 'string') throw new Error('PUSH_REGISTRATION_FAILED');
    const result = await request('/devices', { method: 'POST', token: owner.token, body: device }); check(version);
    if (!/^[a-f0-9]{64}$/.test(result.refresh_secret) || !Number.isFinite(result.refresh_expires_at)) throw new Error('PUSH_REGISTRATION_FAILED');
    await transport.call('confirm', { binding_version: device.binding_version, user_id: owner.userId,
      token_version: device.token_version, refresh_secret: result.refresh_secret, refresh_expires_at: result.refresh_expires_at }); check(version);
    publish({ registration: 'registered' });
  }
  async function register(prompt = false, changed = false) {
    if (!transport.available() || disposed) {
      if (!disposed) void refreshStatus();
      if (prompt) throw new Error('PUSH_REGISTRATION_UNAVAILABLE');
      return;
    }
    if (!session) { if (prompt) throw new Error('PUSH_SESSION_CHANGED'); return; }
    // Coalesce routine checks, but drain token changes and explicit permission requests.
    if (pending) {
      pending.dirty ||= changed; pending.prompt ||= prompt;
      return pending.promise;
    }
    const version = generation, entry = { prompt, dirty: false, promise: null }; pending = entry;
    entry.promise = (async () => {
      do {
        check(version);
        const ask = entry.prompt; entry.prompt = false; entry.dirty = false;
        await perform(ask, version); check(version);
      } while (entry.dirty || entry.prompt);
    })();
    try { await entry.promise; }
    catch (error) {
      if (current(version) && !['PUSH_PERMISSION_DENIED', 'PUSH_PERMISSION_CANCELLED', 'PUSH_SETTINGS_REQUIRED'].includes(error.message)) publish({ registration: 'error' });
      throw error;
    }
    finally { if (pending === entry) pending = null; }
  }
  const connected = async () => {
    const version = generation;
    visible();
    const action = await transport.call('action');
    if (disposed) return;
    const id = Number(action.notification_id), userId = Number(action.recipient_id);
    if (Number.isSafeInteger(id) && id > 0 && Number.isSafeInteger(userId) && userId > 0) onAction({ id, userId });
    check(version);
    if (!session) await unregister(null, version);
    else await register(false);
  };
  const unsubscribe = transport.subscribe(event => {
    if (event === 'ready') { generation++; pending = null; void connected().catch(() => {}); }
    if (event === 'wiw:push:received') onReceived();
    if (event === 'wiw:push:token-changed') void register(false, true).catch(() => {});
    if (event === 'wiw:push:state-changed') {
      void refreshStatus();
      // Returning from Android Settings may not fire a web visibility event.
      void register(false).catch(() => {});
    }
  });
  void refreshStatus();
  if (transport.available()) void connected().catch(() => {});
  document.addEventListener('visibilitychange', visible);
  const timer = setInterval(visible, 15000);
  return {
    async setSession(next) {
      if (next?.token === session?.token && next?.userId === session?.userId) return;
      const previous = session; session = next; const version = ++generation; pending = null;
      publish({ registration: 'unregistered' });
      // Always clear the native previous owner before binding a new session.
      if (transport.available()) await unregister(previous, version);
      if (next && current(version)) await register(false);
    },
    register,
    refreshStatus,
    async openSettings() { await transport.call('settings'); },
    async dispose() {
      disposed = true; generation++; unsubscribe(); clearInterval(timer);
      document.removeEventListener('visibilitychange', visible);
      if (transport.available()) await transport.call('visible', { visible: false }).catch(() => {});
    },
  };
}
